<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { api } from '../../../framework/js/api.js';
    import {
        AGENT_OPTIONS,
        contentKindGuidesForAdmin,
        contentKindLabel,
        contentKindScopeBadge,
        agentLabel,
        knowledgeScopeExplanation,
        knowledgeScopeHeading,
        type ContentKindGuide,
        type KnowledgeAgent,
        type KnowledgeContentKind,
        type KnowledgeScopeAdmin,
        type KnowledgeSourceItem,
        type KnowledgeValidationResult,
    } from './knowledgeAdmin';

    export let open = false;
    export let saving = false;
    export let editing: KnowledgeSourceItem | null = null;
    export let scope: KnowledgeScopeAdmin = 'platform';
    export let scopeLabel = '';

    const dispatch = createEventDispatcher<{
        close: void;
        save: {
            title: string;
            body_text: string;
            content_kind: KnowledgeContentKind;
            pin_in_prompt: boolean;
            retrieval_priority: number;
            agent_allowlist: KnowledgeAgent[];
            file: File | null;
            allow_override: boolean;
        };
    }>();

    const kindGuides = contentKindGuidesForAdmin();
    const totalSteps = 4;
    const stepLabels = ['Content kind', 'Settings', 'Content', 'Review'];

    let step = 1;
    let title = '';
    let bodyText = '';
    let contentKind: KnowledgeContentKind | null = null;
    let pinInPrompt = false;
    let retrievalPriority = 50;
    let selectedAgents: KnowledgeAgent[] = [];
    let file: File | null = null;
    let fileInput: HTMLInputElement | null = null;
    let validating = false;
    let validationError = '';
    let validationResult: KnowledgeValidationResult | null = null;
    let proceedConfirmed = false;

    $: selectedGuide = kindGuides.find((g) => g.value === contentKind) ?? null;
    $: isEdit = editing !== null;
    $: modalTitle = isEdit ? 'Edit knowledge source' : 'Add knowledge source';
    $: scopeHeading = knowledgeScopeHeading(scope, scopeLabel || undefined);
    $: scopeExplanation = knowledgeScopeExplanation(scope, scopeLabel || undefined);
    $: agentSummary =
        selectedAgents.length === 0
            ? 'All agents'
            : selectedAgents.map((a) => agentLabel(a)).join(', ');
    $: canGoNext =
        step === 1
            ? contentKind !== null
            : step === 2
              ? true
              : step === 3
                ? title.trim().length > 0 && (bodyText.trim().length > 0 || file !== null || isEdit)
                : !validating &&
                  !validationError &&
                  validationResult !== null &&
                  (!validationResult.block_upload &&
                      (!validationResult.needs_confirmation || proceedConfirmed));
    $: primaryLabel =
        step < totalSteps
            ? 'Continue'
            : validationResult?.needs_confirmation
              ? 'Proceed anyway'
              : isEdit
                ? 'Save changes'
                : 'Create & publish';

    function resetForm(): void {
        step = 1;
        title = '';
        bodyText = '';
        contentKind = null;
        pinInPrompt = false;
        retrievalPriority = 50;
        selectedAgents = [];
        file = null;
        validating = false;
        validationError = '';
        validationResult = null;
        proceedConfirmed = false;
        if (fileInput) fileInput.value = '';
    }

    $: if (open && editing) {
        step = 1;
        title = editing.title;
        bodyText = editing.body_text ?? '';
        contentKind = editing.content_kind;
        pinInPrompt = editing.pin_in_prompt;
        retrievalPriority = editing.retrieval_priority;
        selectedAgents = editing.agent_allowlist ? [...editing.agent_allowlist] : [];
        file = null;
        validating = false;
        validationError = '';
        validationResult = null;
        proceedConfirmed = false;
    }

    $: if (open && !editing) {
        resetForm();
    }

    function closeModal(): void {
        if (saving || validating) return;
        dispatch('close');
    }

    function handleBackdropKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') closeModal();
    }

    function selectKind(guide: ContentKindGuide): void {
        contentKind = guide.value;
        if (guide.value === 'rubric' || guide.value === 'methodology') {
            pinInPrompt = true;
        }
    }

    function toggleAgent(agent: KnowledgeAgent): void {
        if (selectedAgents.includes(agent)) {
            selectedAgents = selectedAgents.filter((a) => a !== agent);
        } else {
            selectedAgents = [...selectedAgents, agent];
        }
    }

    function onFileChange(event: Event): void {
        const input = event.currentTarget as HTMLInputElement;
        file = input.files?.[0] ?? null;
        validationResult = null;
        proceedConfirmed = false;
    }

    function onBodyInput(): void {
        validationResult = null;
        proceedConfirmed = false;
    }

    function goBack(): void {
        if (step > 1) {
            if (step === 4) {
                validationResult = null;
                validationError = '';
                proceedConfirmed = false;
            }
            step -= 1;
        }
    }

    async function runValidation(): Promise<boolean> {
        if (!contentKind) return false;

        validating = true;
        validationError = '';
        validationResult = null;
        proceedConfirmed = false;

        const hasNewContent = bodyText.trim().length > 0 || file !== null;
        if (isEdit && !hasNewContent && editing?.file_name && !editing.body_text) {
            validationResult = {
                fit_level: 'good',
                warnings: [],
                needs_confirmation: false,
                block_upload: false,
                preview_excerpt: '',
                word_count: 0,
                validated_source: 'unchanged_file',
                quality_score: 'medium',
            };
            validating = false;
            return true;
        }

        try {
            const formData = new FormData();
            const textPayload = bodyText.trim() || (isEdit ? (editing?.body_text ?? '') : '');
            if (textPayload) formData.append('body_text', textPayload);
            formData.append('content_kind', contentKind);
            if (file) formData.append('file', file);

            if (!textPayload && !file) {
                validationError = 'Add text or a file before continuing.';
                return false;
            }

            const response: any = await api.validateAdminKnowledgeContent(formData);
            if (response?.success && response.data) {
                validationResult = response.data as KnowledgeValidationResult;
                return true;
            }

            validationError = response?.message || 'Validation failed. Try again or go back to edit your content.';
            return false;
        } catch (error) {
            console.error(error);
            validationError = 'Could not validate content. Check your connection and try again.';
            return false;
        } finally {
            validating = false;
        }
    }

    async function goNext(): Promise<void> {
        if (!canGoNext) return;

        if (step === 3) {
            if (!title.trim() || !contentKind) return;
            if (!isEdit && !bodyText.trim() && !file) return;
            const ok = await runValidation();
            if (!ok) {
                step = 4;
                return;
            }
            step = 4;
            return;
        }

        if (step < totalSteps) {
            step += 1;
            return;
        }

        submit();
    }

    function submit(): void {
        if (!title.trim() || !contentKind || !validationResult) return;
        if (!isEdit && !bodyText.trim() && !file) return;
        if (validationResult.block_upload) return;
        if (validationResult.needs_confirmation && !proceedConfirmed) return;

        dispatch('save', {
            title: title.trim(),
            body_text: bodyText.trim(),
            content_kind: contentKind,
            pin_in_prompt: pinInPrompt,
            retrieval_priority: retrievalPriority,
            agent_allowlist: selectedAgents,
            file,
            allow_override: proceedConfirmed,
        });
    }
</script>

{#if open}
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div
        class="modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="knowledge-modal-title"
        tabindex="-1"
        on:keydown={handleBackdropKeydown}
    >
        <button type="button" class="modal-backdrop" aria-label="Close dialog" on:click={closeModal}></button>
        <div class="modal-content">
            <header class="modal-header">
                <div>
                    <h2 id="knowledge-modal-title">{modalTitle}</h2>
                    <p class="step-indicator" aria-live="polite">
                        Step {step} of {totalSteps}: {stepLabels[step - 1]}
                    </p>
                </div>
                <button type="button" class="icon-btn" aria-label="Close" on:click={closeModal}>
                    <i class="close icon" aria-hidden="true"></i>
                </button>
            </header>

            <ol class="stepper" aria-label="Progress">
                {#each stepLabels as label, index (label)}
                    <li
                        class="stepper-item"
                        class:active={step === index + 1}
                        class:done={step > index + 1}
                    >
                        <span class="stepper-dot" aria-hidden="true">{index + 1}</span>
                        <span class="stepper-label">{label}</span>
                    </li>
                {/each}
            </ol>

            <aside class="scope-banner" aria-label="Knowledge scope">
                <div class="scope-banner-head">
                    <i class="globe icon" aria-hidden="true"></i>
                    <div>
                        <p class="scope-banner-label">Adding to</p>
                        <p class="scope-banner-title">{scopeHeading}</p>
                    </div>
                </div>
                <p class="scope-banner-desc">{scopeExplanation}</p>
            </aside>

            <div class="modal-body">
                {#if step === 1}
                    <p class="step-intro">
                        Choose <strong>how</strong> the AI should use this content. <strong>Where</strong> it applies
                        is already set above.
                    </p>
                    <div class="kind-grid" role="radiogroup" aria-label="Content kind">
                        {#each kindGuides as guide (guide.value)}
                            {@const scopeBadge = contentKindScopeBadge(guide, scope)}
                            <button
                                type="button"
                                class="kind-card"
                                class:selected={contentKind === guide.value}
                                role="radio"
                                aria-checked={contentKind === guide.value}
                                on:click={() => selectKind(guide)}
                            >
                                <span class="kind-card-header">
                                    <i class="{guide.icon} icon" aria-hidden="true"></i>
                                    <span class="kind-card-title">{guide.label}</span>
                                    {#if scopeBadge}
                                        <span class="kind-scope-badge">{scopeBadge}</span>
                                    {/if}
                                </span>
                                <span class="kind-card-desc">{guide.description}</span>
                                <span class="kind-card-when">
                                    <strong>When to use:</strong> {guide.whenToUse}
                                </span>
                            </button>
                        {/each}
                    </div>
                {:else if step === 2}
                    {#if selectedGuide}
                        <div class="kind-summary">
                            <i class="{selectedGuide.icon} icon" aria-hidden="true"></i>
                            <span>{selectedGuide.label}</span>
                            <button type="button" class="link-btn" on:click={() => (step = 1)}>Change</button>
                        </div>
                    {/if}

                    <p class="step-intro">
                        Set retrieval behaviour before adding content, so it is clear what this source is for.
                    </p>

                    <div class="field">
                        <label class="checkbox-label">
                            <input type="checkbox" bind:checked={pinInPrompt} />
                            Pin in prompt (always include for matching agents)
                        </label>
                    </div>

                    <div class="field">
                        <label for="ks-priority">Retrieval priority (0–100)</label>
                        <input id="ks-priority" type="number" min="0" max="100" bind:value={retrievalPriority} />
                        <p class="hint">Higher priority chunks are kept when the context budget is tight.</p>
                    </div>

                    <fieldset class="field">
                        <legend>Limit to specific agents (leave empty for all)</legend>
                        <div class="agent-grid">
                            {#each AGENT_OPTIONS as opt (opt.value)}
                                <label class="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={selectedAgents.includes(opt.value)}
                                        on:change={() => toggleAgent(opt.value)}
                                    />
                                    {opt.label}
                                </label>
                            {/each}
                        </div>
                    </fieldset>
                {:else if step === 3}
                    {#if selectedGuide}
                        <div class="kind-summary">
                            <i class="{selectedGuide.icon} icon" aria-hidden="true"></i>
                            <span>{selectedGuide.label}</span>
                            <button type="button" class="link-btn" on:click={() => (step = 1)}>Change kind</button>
                        </div>
                    {/if}

                    <p class="step-intro">
                        Add the {contentKind ? contentKindLabel(contentKind).toLowerCase() : 'knowledge'} content.
                        We will check it on the next step before publishing.
                    </p>

                    <div class="field">
                        <label for="ks-title">Title</label>
                        <input
                            id="ks-title"
                            type="text"
                            bind:value={title}
                            placeholder="e.g. GapFinder dimension rubric v2"
                            required
                        />
                    </div>

                    <div class="field">
                        <label for="ks-body">Text content</label>
                        <textarea
                            id="ks-body"
                            rows="7"
                            bind:value={bodyText}
                            on:input={onBodyInput}
                            placeholder="Paste the knowledge text here…"
                        ></textarea>
                    </div>

                    <div class="field">
                        <label for="ks-file">Or upload a file (PDF, DOCX, TXT…)</label>
                        <input
                            id="ks-file"
                            type="file"
                            bind:this={fileInput}
                            accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
                            on:change={onFileChange}
                        />
                        {#if editing?.file_name}
                            <p class="hint">Current file: {editing.file_name}. Upload to replace and re-index.</p>
                        {:else}
                            <p class="hint">Provide text, a file, or both. At least one is required.</p>
                        {/if}
                    </div>
                {:else}
                    <p class="step-intro">We checked your content against the selected kind before publishing.</p>

                    {#if validating}
                        <div class="validation-state loading" role="status">
                            <i class="circle notch loading icon" aria-hidden="true"></i>
                            <span>Checking your content…</span>
                        </div>
                    {:else if validationError}
                        <div class="validation-state error" role="alert">
                            <i class="exclamation triangle icon" aria-hidden="true"></i>
                            <p>{validationError}</p>
                            <button type="button" class="link-btn" on:click={() => runValidation()}>Try again</button>
                        </div>
                    {:else if validationResult}
                        {#if validationResult.fit_level === 'good' && validationResult.warnings.length === 0}
                            <div class="validation-state success" role="status">
                                <i class="check circle icon" aria-hidden="true"></i>
                                <p>
                                    {#if validationResult.validated_source === 'unchanged_file'}
                                        Existing file unchanged. Metadata updates can be published.
                                    {:else}
                                        Content looks appropriate for
                                        <strong>{contentKind ? contentKindLabel(contentKind) : 'this kind'}</strong>.
                                    {/if}
                                </p>
                            </div>
                        {:else}
                            <div
                                class="validation-state"
                                class:warn={!validationResult.block_upload}
                                class:error={validationResult.block_upload}
                                role="alert"
                            >
                                <i
                                    class="{validationResult.block_upload
                                        ? 'ban'
                                        : 'exclamation circle'} icon"
                                    aria-hidden="true"
                                ></i>
                                <div class="validation-messages">
                                    {#each validationResult.warnings as warning (warning.code)}
                                        <p>{warning.message}</p>
                                    {/each}
                                </div>
                            </div>

                            {#if validationResult.preview_excerpt}
                                <div class="excerpt-card">
                                    <h3>Content preview</h3>
                                    <p>{validationResult.preview_excerpt}{validationResult.preview_excerpt.length >= 280 ? '…' : ''}</p>
                                    <span class="excerpt-meta">
                                        {validationResult.word_count} words · checked from {validationResult.validated_source.replaceAll('_', ' ')}
                                    </span>
                                </div>
                            {/if}

                            {#if validationResult.needs_confirmation && !validationResult.block_upload}
                                <label class="confirm-label">
                                    <input type="checkbox" bind:checked={proceedConfirmed} />
                                    I understand — publish this content anyway
                                </label>
                            {/if}

                            {#if validationResult.block_upload}
                                <p class="hint blocked-hint">Go back to edit the content or change the content kind.</p>
                            {/if}
                        {/if}

                        <div class="review-card">
                            <h3>Summary</h3>
                            <dl>
                                <div>
                                    <dt>Scope</dt>
                                    <dd>{scopeHeading}</dd>
                                </div>
                                <div>
                                    <dt>Kind</dt>
                                    <dd>{contentKind ? contentKindLabel(contentKind) : '—'}</dd>
                                </div>
                                <div>
                                    <dt>Title</dt>
                                    <dd>{title || '—'}</dd>
                                </div>
                                <div>
                                    <dt>Agents</dt>
                                    <dd>{agentSummary}</dd>
                                </div>
                                <div>
                                    <dt>Pinned</dt>
                                    <dd>{pinInPrompt ? 'Yes' : 'No'}</dd>
                                </div>
                                <div>
                                    <dt>Content</dt>
                                    <dd>
                                        {#if file}
                                            File: {file.name}
                                        {:else if bodyText.trim()}
                                            Text ({bodyText.trim().length} characters)
                                        {:else if editing?.file_name}
                                            Existing file: {editing.file_name}
                                        {:else}
                                            —
                                        {/if}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    {/if}
                {/if}
            </div>

            <footer class="modal-footer">
                {#if step > 1}
                    <button type="button" class="btn secondary" on:click={goBack} disabled={saving || validating}>
                        Back
                    </button>
                {:else}
                    <button type="button" class="btn secondary" on:click={closeModal} disabled={saving}>Cancel</button>
                {/if}
                <button
                    type="button"
                    class="btn primary"
                    on:click={goNext}
                    disabled={saving || validating || (step === 4 ? !canGoNext : !canGoNext)}
                >
                    {#if saving}
                        Saving…
                    {:else if validating}
                        Checking…
                    {:else}
                        {primaryLabel}
                    {/if}
                </button>
            </footer>
        </div>
    </div>
{/if}

<style>
    .modal-overlay {
        position: fixed;
        inset: 0;
        z-index: 2000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-4);
    }

    .modal-backdrop {
        position: absolute;
        inset: 0;
        border: 0;
        background: var(--shade-background-color);
        cursor: pointer;
    }

    .modal-content {
        position: relative;
        width: min(720px, 100%);
        max-height: 90vh;
        overflow: auto;
        background: var(--white);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-modal);
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: var(--space-4);
        padding: var(--space-5);
        border-bottom: 1px solid var(--border-color);
    }

    .modal-header h2 {
        margin: 0 0 var(--space-1);
        font-size: var(--text-xl);
        font-weight: 500;
        color: var(--grey);
    }

    .step-indicator {
        margin: 0;
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .icon-btn {
        border: 0;
        background: transparent;
        cursor: pointer;
        color: var(--grey-secondary);
        padding: var(--space-2);
        border-radius: var(--radius-button);
        flex-shrink: 0;
    }

    .icon-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .stepper {
        display: flex;
        gap: var(--space-2);
        list-style: none;
        margin: 0;
        padding: var(--space-4) var(--space-5) 0;
    }

    .stepper-item {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-1);
        text-align: center;
        color: var(--grey-secondary);
        font-size: var(--text-xs);
    }

    .stepper-item.active {
        color: var(--primary);
        font-weight: 500;
    }

    .stepper-item.done {
        color: var(--grey);
    }

    .stepper-dot {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.75rem;
        height: 1.75rem;
        border-radius: 999px;
        border: 2px solid var(--border-color);
        font-size: var(--text-xs);
        font-weight: 500;
    }

    .stepper-item.active .stepper-dot {
        border-color: var(--primary);
        background: var(--primary);
        color: var(--white);
    }

    .stepper-item.done .stepper-dot {
        border-color: var(--status-success);
        color: var(--status-success-ink);
    }

    .scope-banner {
        margin: var(--space-4) var(--space-5) 0;
        padding: var(--space-4);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        background: var(--status-info-soft);
    }

    .scope-banner-head {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        color: var(--status-info-ink);
    }

    .scope-banner-head .icon {
        margin-top: 0.15rem;
    }

    .scope-banner-label {
        margin: 0;
        font-size: var(--text-xs);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--grey-secondary);
    }

    .scope-banner-title {
        margin: 0;
        font-weight: 500;
        color: var(--grey);
    }

    .scope-banner-desc {
        margin: var(--space-2) 0 0;
        font-size: var(--text-sm);
        color: var(--grey);
    }

    .modal-body {
        padding: var(--space-5);
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
    }

    .step-intro {
        margin: 0;
        color: var(--grey-secondary);
    }

    .kind-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--space-3);
    }

    .kind-card {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        text-align: left;
        padding: var(--space-4);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-card);
        background: var(--white);
        cursor: pointer;
        font: inherit;
        color: var(--grey);
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .kind-card:hover {
        border-color: var(--primary);
    }

    .kind-card.selected {
        border-color: var(--primary);
        box-shadow: var(--focus-ring);
    }

    .kind-card:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .kind-card-header {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--space-2);
        font-weight: 500;
        color: var(--grey);
    }

    .kind-scope-badge {
        margin-left: auto;
        font-size: var(--text-xs);
        font-weight: 400;
        padding: 0.125rem 0.5rem;
        border-radius: 999px;
        background: var(--greyish);
        color: var(--grey-secondary);
    }

    .kind-card.selected .kind-scope-badge {
        background: var(--status-info-soft);
        color: var(--status-info-ink);
    }

    .kind-card-desc {
        font-size: var(--text-sm);
        color: var(--grey);
    }

    .kind-card-when {
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .kind-summary {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-4);
        background: var(--greyish);
        border-radius: var(--radius-button);
        font-weight: 500;
        color: var(--grey);
    }

    .link-btn {
        margin-left: auto;
        border: 0;
        background: transparent;
        color: var(--primary);
        text-decoration: underline;
        cursor: pointer;
        font: inherit;
    }

    .link-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
    }

    label,
    legend {
        font-weight: 400;
        color: var(--grey);
    }

    input[type='text'],
    input[type='number'],
    textarea {
        width: 100%;
        box-sizing: border-box;
        padding: 0.75rem 1rem;
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius-button);
        background: rgb(var(--input-bg));
        font: inherit;
    }

    input:focus-visible,
    textarea:focus-visible {
        outline: none;
        border-color: var(--primary);
        box-shadow: var(--focus-ring);
    }

    fieldset {
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius-button);
        padding: var(--space-3);
        margin: 0;
    }

    .agent-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--space-2);
    }

    .checkbox-label,
    .confirm-label {
        display: flex;
        align-items: flex-start;
        gap: var(--space-2);
        font-weight: 300;
    }

    .confirm-label {
        padding: var(--space-3) var(--space-4);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-button);
        background: var(--greyish);
        color: var(--grey);
    }

    .hint {
        margin: 0;
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .blocked-hint {
        color: var(--status-danger-ink);
    }

    .validation-state {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        padding: var(--space-4);
        border-radius: var(--radius-card);
        border: 1px solid var(--border-color);
    }

    .validation-state p {
        margin: 0;
        color: var(--grey);
    }

    .validation-state.loading {
        background: var(--greyish);
    }

    .validation-state.success {
        background: var(--status-success-soft);
        border-color: var(--status-success);
        color: var(--status-success-ink);
    }

    .validation-state.success p {
        color: var(--status-success-ink);
    }

    .validation-state.warn {
        background: var(--status-warn-soft);
        border-color: var(--status-warn);
    }

    .validation-state.error {
        background: var(--status-danger-soft);
        border-color: var(--status-danger);
    }

    .validation-messages {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
    }

    .excerpt-card {
        padding: var(--space-4);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        background: var(--white);
    }

    .excerpt-card h3 {
        margin: 0 0 var(--space-2);
        font-size: var(--text-sm);
        font-weight: 500;
        color: var(--grey-secondary);
    }

    .excerpt-card p {
        margin: 0;
        font-size: var(--text-sm);
        color: var(--grey);
        white-space: pre-wrap;
    }

    .excerpt-meta {
        display: block;
        margin-top: var(--space-2);
        font-size: var(--text-xs);
        color: var(--grey-secondary);
    }

    .review-card {
        padding: var(--space-4);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        background: var(--greyish);
    }

    .review-card h3 {
        margin: 0 0 var(--space-3);
        font-size: var(--text-base);
        font-weight: 500;
        color: var(--grey);
    }

    .review-card dl {
        margin: 0;
        display: grid;
        gap: var(--space-2);
    }

    .review-card div {
        display: grid;
        grid-template-columns: 5rem 1fr;
        gap: var(--space-2);
        font-size: var(--text-sm);
    }

    .review-card dt {
        color: var(--grey-secondary);
        font-weight: 400;
    }

    .review-card dd {
        margin: 0;
        color: var(--grey);
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-3);
        padding: var(--space-4) var(--space-5);
        border-top: 1px solid var(--border-color);
    }

    .btn {
        border: 0;
        border-radius: var(--border-radius-button);
        padding: var(--space-2) var(--space-4);
        font-weight: 500;
        cursor: pointer;
    }

    .btn.primary {
        background: var(--primary);
        color: var(--white);
    }

    .btn.secondary {
        background: var(--greyish);
        color: var(--grey);
    }

    .btn:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    @media (max-width: 768px) {
        .kind-grid,
        .agent-grid {
            grid-template-columns: 1fr;
        }

        .stepper-label {
            display: none;
        }
    }
</style>
